import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../App";
import { Button } from "../ui/button";
import {
  Users, Wrench, LayoutDashboard, LogOut,
  Shield, Clock, Settings, Server, Flame,
  ChevronLeft, ChevronRight, Radio, Menu, X, Gift
} from "lucide-react";
import { APP_VERSION } from "../../version";

export const Sidebar = ({ collapsed, setCollapsed, mobileOpen, setMobileOpen }) => {
  const { logout, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const navItems = [
    { path: "/admin", icon: LayoutDashboard, label: "Dashboard", exact: true },
    { path: "/admin/users", icon: Users, label: "Utilisateurs" },
    { path: "/admin/tools", icon: Wrench, label: "Outils" },
    { path: "/admin/providers", icon: Server, label: "Fournisseurs" },
    { path: "/admin/firestick", icon: Flame, label: "Firestick Codes" },
    { path: "/admin/gift-codes", icon: Gift, label: "Codes Gift" },
    { path: "/admin/logs", icon: Clock, label: "Historique" },
    { path: "/admin/m3u-history", icon: Radio, label: "Bot M3U" },
    { path: "/admin/settings", icon: Settings, label: "Parametres" },
  ];

  const handleNavClick = () => {
    if (mobileOpen) setMobileOpen(false);
  };

  const sidebarContent = (
    <>
      {/* Header */}
      <div className="p-4 border-b border-border flex items-center justify-between">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            <span className="font-bold text-lg tracking-tight">ADMIN</span>
          </div>
        )}
        <button
          onClick={() => {
            if (window.innerWidth < 768) {
              setMobileOpen(false);
            } else {
              setCollapsed(!collapsed);
            }
          }}
          className="p-1 hover:bg-muted rounded-sm"
          data-testid="toggle-sidebar"
        >
          {window.innerWidth < 768 ? (
            <X className="h-5 w-5" />
          ) : collapsed ? (
            <ChevronRight className="h-5 w-5" />
          ) : (
            <ChevronLeft className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-2 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = item.exact
            ? location.pathname === item.path
            : location.pathname.startsWith(item.path);

          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={handleNavClick}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-sm mb-1 transition-colors ${
                isActive
                  ? "bg-primary/10 text-primary border-l-2 border-primary"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              {(!collapsed || window.innerWidth < 768) && <span className="font-medium">{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* User info & Logout */}
      <div className="p-4 border-t border-border">
        {(!collapsed || window.innerWidth < 768) && (
          <div className="mb-3">
            <p className="text-sm font-medium truncate">{user?.login}</p>
            <p className="text-xs text-muted-foreground">Administrateur</p>
          </div>
        )}
        <Button
          variant="ghost"
          size={collapsed && window.innerWidth >= 768 ? "icon" : "default"}
          onClick={handleLogout}
          className="w-full justify-start text-muted-foreground hover:text-destructive hover:bg-destructive/10"
          data-testid="logout-button"
        >
          <LogOut className="h-5 w-5" />
          {(!collapsed || window.innerWidth < 768) && <span className="ml-2">Deconnexion</span>}
        </Button>
        {(!collapsed || window.innerWidth < 768) && (
          <p
            className="mt-3 text-center text-[10px] text-muted-foreground/50 font-mono"
            data-testid="app-version-sidebar"
          >
            PanelHelper {APP_VERSION}
          </p>
        )}
      </div>
    </>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className={`hidden md:flex bg-card border-r border-border h-screen sticky top-0 flex-col transition-all duration-300 ${
          collapsed ? "w-16" : "w-64"
        }`}
        data-testid="admin-sidebar"
      >
        {sidebarContent}
      </aside>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-card border-r border-border z-50 flex flex-col transition-transform duration-300 md:hidden ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="admin-sidebar-mobile"
      >
        {sidebarContent}
      </aside>
    </>
  );
};

// Mobile header with hamburger menu
export const MobileHeader = ({ onMenuClick }) => (
  <div className="md:hidden flex items-center gap-3 p-3 border-b border-border bg-card sticky top-0 z-30">
    <button
      onClick={onMenuClick}
      className="p-2 hover:bg-muted rounded-sm"
      data-testid="mobile-menu-btn"
    >
      <Menu className="h-5 w-5" />
    </button>
    <Shield className="h-5 w-5 text-primary" />
    <span className="font-bold text-sm">ADMIN</span>
  </div>
);
